import { addCallback } from '../../../lib/teact/teactn';

import type { Thread, ThreadId, ThreadLocalState } from '../../../types';
import type { RequiredGlobalActions } from '../../index';
import type { ActionReturnType, GlobalState } from '../../types';
import { MAIN_THREAD_ID } from '../../../api/types';

import { DEBUG, MESSAGE_LIST_SLICE, SERVICE_NOTIFICATIONS_USER_ID } from '../../../config';
import { getCurrentTabId } from '../../../util/establishMultitabRole';
import { init as initFolderManager } from '../../../util/folderManager';
import {
  buildCollectionByKey, omitUndefined, pick, unique,
} from '../../../util/iteratees';
import { isLocalMessageId } from '../../../util/keys/messageKey';
import { callApi } from '../../../api/client';
import { getIsSavedDialog, isMessageLocal, orderHistoryIds } from '../../helpers';
import {
  addActionHandler, getActions, getGlobal, setGlobal,
} from '../../index';
import { getRecentlyViewedThreads, markThreadsAwaitingRefresh } from '../../recentlyViewedThreads';
import {
  addChatMessagesById,
  safeReplaceViewportIds,
  updateChats,
  updateListedIds,
  updateTopicsInfo,
  updateUsers,
} from '../../reducers';
import { updateTabState } from '../../reducers/tabs';
import {
  replaceThreadLocalStateParam,
  updateThreadInfo,
  updateThreadLocalState,
  updateThreadReadState,
} from '../../reducers/threads';
import {
  selectChat,
  selectChatByClientOrRawId,
  selectChatMessage,
  selectChatMessages,
  selectCurrentMessageList,
  selectIsViewportNewest,
  selectTabState,
  selectTopics,
  selectViewportIds,
} from '../../selectors';
import {
  selectDraft,
  selectEditingDraft,
  selectEditingId,
  selectThreadInfo,
  selectThreadReadState,
} from '../../selectors/threads';
import { getIsLandedSendTwin } from '../apiUpdaters/messages';

type MessageListPosition = {
  chatId: string;
  threadId: ThreadId;
  isAtTail: boolean;
};

const RELEASE_STATUS_TIMEOUT = 15000; // 15 sec;
// The difference log carries messages, read state and pinned ids — not chat renames, pins, mutes,
// archiving or folder moves. Those only arrive with a full chat-list load, so one still runs at this
// interval however well the incremental path is doing. Drop this once the backend stamps chat-level
// mutations with a `seq` too.
const FULL_SYNC_INTERVAL = 10 * 60 * 1000; // 10 min

let releaseStatusTimeout: number | undefined;
// Guards against two resyncs overlapping; released by `whenFirstBatchDone` or by the watchdog above
let isSyncInFlight = false;
// A resync that arrived while one was already running, replayed once the flag clears
let isSyncPending = false;

// Where each tab's message list stood when the pass began. It has to be read this early: the full
// path starts with `loadAllChats`, which advances every chat's `lastMessageId`, and after that
// nothing can tell a tab that was sitting on the newest message from one scrolled back through
// history — only the former may be moved forward onto the messages the gap brought in.
let messageListPositionByTabId: Record<number, MessageListPosition> = {};

function releaseSyncFlag(actions: RequiredGlobalActions) {
  isSyncInFlight = false;
  if (!isSyncPending) return;

  isSyncPending = false;
  actions.sync();
}

addActionHandler('sync', (global, actions): ActionReturnType => {
  if (DEBUG) {
    // eslint-disable-next-line no-console
    console.log('>>> START SYNC');
  }

  // A flapping connection fires `requestSync` per re-subscribe, and each pass ends in
  // `loadAndReplaceMessages`, which REPLACES the message store from what it fetched. Two passes
  // overlapping means the slower one lands last and overwrites the newer one's results with its own
  // older snapshot. One at a time; the next reconnect will start a fresh pass anyway.
  if (isSyncInFlight) {
    // Remember it instead of dropping it. The request that arrives mid-pass is usually the one that
    // matters — a second reconnect means the first pass was reading from a socket that had already
    // died — so it is re-dispatched when this pass releases the flag.
    isSyncPending = true;

    if (DEBUG) {
      // eslint-disable-next-line no-console
      console.log('>>> SKIP SYNC (already running, queued)');
    }

    return;
  }

  isSyncInFlight = true;
  isSyncPending = false;

  if (releaseStatusTimeout) {
    clearTimeout(releaseStatusTimeout);
  }

  global = getGlobal();
  global = { ...global, isSyncing: true };
  setGlobal(global);

  captureMessageListPositions(global);

  // Workaround for `isSyncing = true` sometimes getting stuck for some reason. It doubles as the
  // backstop that releases `isSyncInFlight`: `whenFirstBatchDone` is skipped when the connection drops
  // mid-pass, and a flag left set would block every later sync for the rest of the session.
  releaseStatusTimeout = window.setTimeout(() => {
    global = getGlobal();
    global = { ...global, isSyncing: false };
    setGlobal(global);
    releaseStatusTimeout = undefined;
    releaseSyncFlag(actions);
  }, RELEASE_STATUS_TIMEOUT);

  initFolderManager();

  // Folder changes are not in the difference log, and their only realtime signal is a bare
  // `updateDialogFilters` — one that arrived while the socket was down is gone for good. Re-read the
  // list on every resync, or a folder edited elsewhere stays stale until the app is restarted.
  actions.loadChatFolders();

  void runSync(global, actions);
});

// Resume runs at two speeds. When the backend's difference log can replay everything missed since the
// stored `seq` cursor, that is the whole sync — a handful of events instead of the full chat list
// (`loadAllChats`, up to 200 chats with their users, last messages and read states) plus a message
// repair pass for every open thread. Only when the cursor is unusable, the client is too far behind,
// or the log can't answer does the full path below run.
async function runSync<T extends GlobalState>(global: T, actions: RequiredGlobalActions) {
  // A rejected request (dead worker, transport error) must not leave the sync half-started with its
  // in-flight flag set — treat it as "no difference available" and reload everything.
  const difference = await callApi('fetchDifference', {
    shouldSkipReplay: Date.now() - (global.lastFullSyncAt ?? 0) >= FULL_SYNC_INTERVAL,
  }).catch(() => undefined);

  // The difference only patches a chat list that is already loaded; after a `reset` there is none to patch
  global = getGlobal();
  const isIncremental = difference?.status === 'applied' && Boolean(global.chats.listIds.active)
    && !hasUnknownChats(difference.chatIds);

  if (DEBUG) {
    // eslint-disable-next-line no-console
    console.log(`>>> SYNC ${isIncremental ? 'INCREMENTAL' : 'FULL'}`,
      `(difference: ${difference?.status ?? 'unavailable'}, ${difference?.eventCount ?? 0} event(s))`);
  }

  if (isIncremental) {
    finishSync(actions);
    return;
  }

  runFullSync(global, actions);
}

// The difference log only covers messages, read state and pinned ids — chat creation, renames and
// leaves carry no `seq` yet, so an event for a chat this client has never seen proves the chat list
// itself is stale. Nothing but a full reload can fix that.
function hasUnknownChats(chatIds: string[]) {
  if (!chatIds.length) return false;

  const global = getGlobal();
  // The backend stamps some realtime chat ids in the raw plain-negative form (`-137`) rather than the
  // client channel form (`-1000000000137`), so resolve both ways before calling a chat unknown.
  return chatIds.some((chatId) => !selectChatByClientOrRawId(global, chatId));
}

function runFullSync<T extends GlobalState>(global: T, actions: RequiredGlobalActions) {
  const { loadAllChats, preloadTopChatMessages } = actions;

  global = getGlobal();
  global = { ...global, lastFullSyncAt: Date.now() };
  setGlobal(global);

  loadAllChats({
    listType: 'active',
    whenFirstBatchDone: async () => {
      // Dispatch the archived-list load before the awaited `loadAndReplaceMessages` so a throw there
      // can't skip it — otherwise an empty Archived folder never loads `listIds.archived` and the
      // chat list shows a perpetual spinner instead of the empty state.
      loadAllChats({ listType: 'archived' });

      await loadAndReplaceMessages(global, actions);

      finishSync(actions);

      restoreTailPosition(actions);

      preloadTopChatMessages();
    },
  });
}

function finishSync(actions: RequiredGlobalActions) {
  let global = getGlobal();
  global = {
    ...global,
    isSyncing: false,
    isSynced: true,
    isFetchingDifference: false,
  };
  setGlobal(global);

  // The watchdog has nothing left to release, and leaving it armed would clear the `isSyncing` flag
  // of whichever sync starts next.
  if (releaseStatusTimeout) {
    clearTimeout(releaseStatusTimeout);
    releaseStatusTimeout = undefined;
  }

  releaseSyncFlag(actions);

  if (DEBUG) {
    // eslint-disable-next-line no-console
    console.log('>>> FINISH SYNC');
  }
}

function captureMessageListPositions<T extends GlobalState>(global: T) {
  messageListPositionByTabId = {};

  for (const { id: tabId } of Object.values(global.byTabId)) {
    const currentMessageList = selectCurrentMessageList(global, tabId);
    if (!currentMessageList) continue;

    const { chatId, threadId = MAIN_THREAD_ID } = currentMessageList;
    messageListPositionByTabId[tabId] = {
      chatId,
      threadId,
      isAtTail: Boolean(selectIsViewportNewest(global, chatId, threadId, tabId)),
    };
  }
}

function isTabAtTail(tabId: number, chatId: string, threadId: ThreadId) {
  const position = messageListPositionByTabId[tabId];

  return Boolean(position?.isAtTail && position.chatId === chatId && position.threadId === threadId);
}

// Snapping the viewport forward is not enough on its own. The list only sticks to the bottom when the
// viewport was newest on the *previous* render too, and `loadAllChats` advances the chat's
// `lastMessageId` a beat before the viewport catches up — so that flag reads false exactly when the
// tail lands, and the messages the gap brought in render just below the fold. Ask for the same jump
// the scroll-down button performs. It also refetches the last slice when the repair fetch came back
// empty and left the viewport behind entirely, which a tab can never recover from on its own: the
// forwards trigger sits at the end of the list and is already on screen, so no intersection fires.
function restoreTailPosition(actions: RequiredGlobalActions) {
  const global = getGlobal();

  for (const { id: tabId } of Object.values(global.byTabId)) {
    const { chatId, threadId } = selectCurrentMessageList(global, tabId) || {};
    if (!chatId || !threadId || !isTabAtTail(tabId, chatId, threadId)) continue;

    actions.scrollMessageListToBottom({ tabId });
  }
}

async function loadAndReplaceMessages<T extends GlobalState>(global: T, actions: RequiredGlobalActions) {
  let areMessagesLoaded = false;

  global = getGlobal();

  let wasReset = false;
  const preservedTabThreadsByTabId = preserveTabThreads(global);
  // Ordered weakest to strongest: every chat keeps its last message, the threads the user has been
  // moving between also keep their on-screen slice, and the open chat's full thread data wins over
  // both.
  const preservedCurrentThreadsByChatId = preserveCurrentThreads(
    global, preserveRecentThreads(global, preserveLastMessages(global)),
  );
  // The preserved slices render before the server has confirmed them, so make the next visit to each
  // refresh once
  markThreadsAwaitingRefresh(getRecentlyViewedThreads());

  // Memoize drafts
  const draftChatIds = Object.keys(global.messages.byChatId);
  const draftsByChatId = draftChatIds
    .reduce<Record<string, Record<number, Partial<ThreadLocalState>>>>((acc, chatId) => {
      acc[chatId] = Object
        .keys(global.messages.byChatId[chatId].threadsById)
        .reduce<Record<number, Partial<ThreadLocalState>>>((acc2, threadId) => {
          acc2[Number(threadId)] = omitUndefined({
            draft: selectDraft(global, chatId, Number(threadId)),
            editingId: selectEditingId(global, chatId, Number(threadId)),
            editingDraft: selectEditingDraft(global, chatId, Number(threadId)),
          });

          return acc2;
        }, {});
      return acc;
    }, {});

  const currentTabId = getCurrentTabId();
  const tabs = Object.values(global.byTabId)
    .sort(({ id: leftId }, { id: rightId }) => {
      if (leftId === currentTabId) return -1;
      if (rightId === currentTabId) return 1;
      return 0;
    });

  for (const { id: tabId } of tabs) {
    global = getGlobal();
    const { chatId: currentChatId, threadId: currentThreadId } = selectCurrentMessageList(global, tabId) || {};
    const activeThreadId = currentThreadId || MAIN_THREAD_ID;
    const currentChat = currentChatId ? global.chats.byId[currentChatId] : undefined;
    const currentViewportIds = currentChatId
      ? selectViewportIds(global, currentChatId, activeThreadId, tabId)
      : undefined;
    // Local (unsent/failed) messages have fractional ids with no server counterpart; passing them to
    // `fetchMessagesById` makes the backend reject the whole batch, which would drop the real viewport
    // messages too. Refetch only server ids — local ones are preserved separately below.
    const serverViewportIds = currentViewportIds?.filter((id) => !isLocalMessageId(id));
    const isSavedDialog = currentChatId
      ? getIsSavedDialog(currentChatId, activeThreadId, global.currentUserId)
      : false;
    if (currentChatId && currentChat) {
      const discussionChat = resolveDiscussionChat(global, currentChatId, activeThreadId);
      const [result, resultDiscussion, refreshedViewportMessages] = await Promise.all([
        loadTopMessages(
          global,
          currentChatId,
          activeThreadId,
          currentViewportIds,
        ),
        discussionChat
          ? callApi('fetchDiscussionMessage', {
            chat: discussionChat.chat,
            messageId: discussionChat.messageId,
          }) : undefined,
        serverViewportIds?.length && !isSavedDialog
          ? callApi('fetchMessagesById', {
            chat: currentChat,
            messageIds: serverViewportIds,
          }).catch(() => undefined)
          : undefined,
      ]);
      global = getGlobal();
      const { chatId: newCurrentChatId } = selectCurrentMessageList(global, tabId) || {};

      if (result && newCurrentChatId === currentChatId) {
        const currentChatMessages = selectChatMessages(global, currentChatId);
        const serverMessages = result.messages.concat(refreshedViewportMessages || []);
        const localMessages = currentChatId === SERVICE_NOTIFICATIONS_USER_ID
          ? global.serviceNotifications.filter(({ isDeleted }) => !isDeleted).map(({ message }) => message)
          // Preserve unsent local messages (failed/pending) — server history never returns their
          // fractional ids, so without this they'd be wiped from `byId`/`listedIds` and vanish on F5.
          // A still-"pending" message can't complete after a reload (its worker send is gone), so flip
          // it to failed for a clear state. Drop the ones whose server copy is already here: those sends
          // landed, only their response was lost, and keeping them leaves a duplicate stuck at the bottom.
          : Object.values(currentChatMessages || {})
            .filter((message) => isMessageLocal(message) && !(message.isOutgoing && serverMessages.some(
              (serverMessage) => getIsLandedSendTwin(global, message, serverMessage),
            )))
            .map((message) => (message.sendingState === 'messageSendingStatePending'
              ? { ...message, sendingState: 'messageSendingStateFailed' as const }
              : message));
        const topics = selectTopics(global, currentChatId);
        const topicLastMessages = topics ? Object.values(topics)
          .map(({ id }) => {
            const topicThreadInfo = selectThreadInfo(global, currentChatId, id);
            return topicThreadInfo?.lastMessageId ? currentChatMessages[topicThreadInfo.lastMessageId] : undefined;
          }).filter(Boolean) : [];

        const resultMessageIds = result.messages.map(({ id }) => id);
        const messagesThreads = pick(global.messages.byChatId[currentChatId]?.threadsById ?? {}, resultMessageIds);

        const isDiscussionStartLoaded = !result.messages.length
          || result.messages.some(({ id }) => id === resultDiscussion?.firstMessageId);
        const threadStartMessages = (isDiscussionStartLoaded && resultDiscussion?.topMessages) || [];
        const refreshedViewportIds = refreshedViewportMessages?.map(({ id }) => id) || [];
        const allMessages = threadStartMessages.concat(result.messages, refreshedViewportMessages || [], localMessages);
        const allMessagesWithTopicLastMessages = allMessages.concat(topicLastMessages);
        const byId = buildCollectionByKey(allMessagesWithTopicLastMessages, 'id');
        const listedIds = unique(refreshedViewportIds.concat(allMessages.map(({ id }) => id)));

        if (!wasReset) {
          global = resetMessages(global, preservedCurrentThreadsByChatId);

          Object.values(global.byTabId).forEach(({ id: otherTabId }) => {
            global = updateTabState(global, {
              tabThreads: preservedTabThreadsByTabId[otherTabId] || {},
            }, otherTabId);
          });
          wasReset = true;
        }

        global = addChatMessagesById(global, currentChatId, byId);
        if (resultDiscussion) {
          global = updateThreadInfo(global, resultDiscussion.threadInfo);
          global = updateThreadReadState(global, currentChatId, activeThreadId, resultDiscussion.threadReadState);
          global = replaceThreadLocalStateParam(
            global, currentChatId, activeThreadId, 'firstMessageId', resultDiscussion.firstMessageId,
          );
          global = addChatMessagesById(global, currentChatId, buildCollectionByKey(resultDiscussion.topMessages, 'id'));
        }
        global = updateListedIds(global, currentChatId, activeThreadId, listedIds);

        Object.entries(messagesThreads).forEach(([id, thread]) => {
          if (!thread?.threadInfo) return;
          global = updateThreadInfo(global, thread.threadInfo);
        });

        Object.values(global.byTabId).forEach(({ id: otherTabId }) => {
          const { chatId: otherChatId, threadId: otherThreadId } = selectCurrentMessageList(global, otherTabId) || {};
          if (otherChatId === currentChatId && otherThreadId === activeThreadId) {
            const preservedViewportIds = preservedTabThreadsByTabId[otherTabId]
              ?.[currentChatId]?.[activeThreadId]?.viewportIds;
            const mergedMessagesById = selectChatMessages(global, currentChatId) || {};
            const nextViewportIds = preservedViewportIds?.filter((id) => Boolean(mergedMessagesById[id]));
            // A tab that sat on the newest message has to land on the newest message again. Restoring
            // its pre-sync slice strands it instead: the messages the gap brought in are in `listedIds`
            // but outside the viewport, so nothing renders them, `selectIsViewportNewest` stays false
            // from here on, and that makes `updateListedAndViewportIds` turn away every later realtime
            // message too — the chat freezes on the last message received before the connection dropped
            // until the user leaves and re-enters it. This is the sleep/wake case, which always takes
            // the full path (`FULL_SYNC_INTERVAL` has passed, so the difference log is skipped). A tab
            // scrolled back through history keeps its place.
            let viewportIdsToSet = nextViewportIds?.length ? nextViewportIds : listedIds;
            if (isTabAtTail(otherTabId, currentChatId, activeThreadId)) {
              // Only the newest slice: `listedIds` also carries the pre-sync ids, and when more than a
              // slice arrived while the connection was down the two blocks do not meet — rendering them
              // together would pass a hole off as continuous history.
              viewportIdsToSet = orderHistoryIds([...listedIds]).slice(-MESSAGE_LIST_SLICE);
            }

            global = safeReplaceViewportIds(
              global,
              currentChatId,
              activeThreadId,
              viewportIdsToSet,
              otherTabId,
            );
          }
        });
        global = updateChats(global, buildCollectionByKey(result.chats, 'id'));
        global = updateUsers(global, buildCollectionByKey(result.users, 'id'));

        areMessagesLoaded = true;
      }
    }

    setGlobal(global);

    if (currentChat?.isForum) {
      actions.loadTopics({ chatId: currentChatId!, force: true });
      if (currentThreadId && currentThreadId !== MAIN_THREAD_ID) {
        actions.loadTopicById({
          chatId: currentChatId!, topicId: Number(currentThreadId), shouldCloseChatOnError: true,
        });
      }
    }
  }

  global = getGlobal();

  if (!areMessagesLoaded) {
    global = resetMessages(global, preservedCurrentThreadsByChatId);

    Object.values(global.byTabId).forEach(({ id: otherTabId }) => {
      global = updateTabState(global, {
        tabThreads: preservedTabThreadsByTabId[otherTabId] || {},
      }, otherTabId);
    });
  }

  // Restore drafts
  Object.keys(draftsByChatId).forEach((chatId) => {
    const threads = draftsByChatId[chatId];
    Object.keys(threads).forEach((threadId) => {
      global = updateThreadLocalState(global, chatId, Number(threadId), draftsByChatId[chatId][Number(threadId)]);
    });
  });

  setGlobal(global);

  Object.values(global.byTabId).forEach(({ id: tabId }) => {
    const { chatId: audioChatId, messageId: audioMessageId } = selectTabState(global, tabId).audioPlayer;
    if (audioChatId && audioMessageId && !selectChatMessage(global, audioChatId, audioMessageId)) {
      actions.closeAudioPlayer({ tabId });
    }
  });
}

function resetMessages<T extends GlobalState>(
  global: T,
  preservedByChatId: GlobalState['messages']['byChatId'] = {},
) {
  global = {
    ...global,
    messages: {
      ...global.messages,
      byChatId: preservedByChatId,
    },
  };

  // The reset also drops each topic's thread info, where its last message lives, and a forum whose
  // topic list is complete is never fetched again — its topics stayed without a preview until a reload.
  // Have every forum refetch its topics the next time it is shown
  Object.keys(global.chats.topicsInfoById).forEach((chatId) => {
    global = updateTopicsInfo(global, chatId, { isCache: true });
  });

  return global;
}

// Keeps the open thread's tab state, plus that of the threads the user has recently been in — their
// `viewportIds` are what lets re-entry render from cache instead of waiting on a round trip.
function preserveTabThreads<T extends GlobalState>(global: T) {
  return Object.values(global.byTabId).reduce<Record<number, GlobalState['byTabId'][number]['tabThreads']>>(
    (acc, { id: tabId }) => {
      const { tabThreads } = selectTabState(global, tabId);
      const currentMessageList = selectCurrentMessageList(global, tabId);
      const threadsToKeep = currentMessageList
        ? [{ chatId: currentMessageList.chatId, threadId: currentMessageList.threadId ?? MAIN_THREAD_ID },
          ...getRecentlyViewedThreads()]
        : getRecentlyViewedThreads();

      threadsToKeep.forEach(({ chatId, threadId }) => {
        const tabThread = tabThreads[chatId]?.[threadId];
        if (!tabThread) {
          return;
        }

        acc[tabId] = {
          ...acc[tabId],
          [chatId]: {
            ...acc[tabId]?.[chatId],
            [threadId]: tabThread,
          },
        };
      });

      return acc;
    },
    {},
  );
}

// Keeps the on-screen slice of each recently visited thread across the reset. Only the messages the
// viewport actually holds are kept, so this is bounded by `MESSAGE_LIST_SLICE` per thread rather than
// by how much history the user had scrolled through.
function preserveRecentThreads<T extends GlobalState>(
  global: T, base: GlobalState['messages']['byChatId'],
) {
  const acc = { ...base };

  getRecentlyViewedThreads().forEach(({ chatId, threadId }) => {
    const chatMessages = global.messages.byChatId[chatId];
    const thread = chatMessages?.threadsById[threadId];
    if (!thread) {
      return;
    }

    const viewportIds = selectViewportIdsInAnyTab(global, chatId, threadId);
    const keptIds = viewportIds?.filter((id) => Boolean(chatMessages.byId[id]));
    if (!keptIds?.length) {
      return;
    }

    acc[chatId] = {
      ...acc[chatId],
      byId: {
        ...acc[chatId]?.byId,
        ...pick(chatMessages.byId, keptIds),
      },
      summaryById: {},
      threadsById: {
        ...acc[chatId]?.threadsById,
        [threadId]: {
          ...thread,
          localState: {
            ...thread.localState,
            // `loadViewportMessages` builds its `Around` slice out of `listedIds`, so narrowing them
            // to what was kept is what makes re-entry render without a round trip. Outlying lists
            // point at history that is gone, so they cannot come along.
            listedIds: keptIds,
            outlyingLists: undefined,
          },
        },
      },
    };
  });

  return acc;
}

function selectViewportIdsInAnyTab<T extends GlobalState>(global: T, chatId: string, threadId: ThreadId) {
  return Object.values(global.byTabId)
    .map(({ id: tabId }) => selectViewportIds(global, chatId, threadId, tabId))
    .find((ids) => Boolean(ids?.length));
}

// Merges into `base` per thread rather than replacing the chat: a forum's other topics are kept there
// too, and swapping the whole chat for the open topic dropped them — a topic the user had just left
// came back with its on-screen slice but no `listedIds` or message count, so it could not scroll
function preserveCurrentThreads<T extends GlobalState>(global: T, base: GlobalState['messages']['byChatId']) {
  return Object.values(global.byTabId).reduce<GlobalState['messages']['byChatId']>((acc, { id: tabId }) => {
    const currentMessageList = selectCurrentMessageList(global, tabId);
    if (!currentMessageList) {
      return acc;
    }

    const { chatId, threadId = MAIN_THREAD_ID } = currentMessageList;
    const currentThread = global.messages.byChatId[chatId]?.threadsById[threadId];
    if (!currentThread) {
      return acc;
    }

    acc[chatId] = {
      // Keep the open chat's cached messages so its viewport stays rendered during the sync refetch —
      // the viewport ids are preserved at tab level, but they resolve to nothing (blank flash) if `byId`
      // is wiped. The fresh fetch below merges over these via `addChatMessagesById`/`updateListedIds`,
      // so the final state stays server-authoritative.
      byId: global.messages.byChatId[chatId].byId,
      summaryById: {},
      threadsById: {
        ...acc[chatId]?.threadsById,
        [threadId]: {
          ...currentThread,
          localState: {
            ...currentThread.localState,
            listedIds: undefined,
            outlyingLists: undefined,
          },
        },
      },
    };

    return acc;
  }, { ...base });
}

// Keep each chat's last message across the sync reset. The chat list still holds every chat's
// `lastMessageId`, but `resetMessages` wipes the bodies from `byId`, so `selectChatLastMessage` returns
// `undefined` until `loadAllChats` refetches — flashing the conversation-list previews to a blank/loading
// state. One message per chat is cheap, and the fresh fetch merges over it, so state stays authoritative.
function preserveLastMessages<T extends GlobalState>(global: T): GlobalState['messages']['byChatId'] {
  const acc: GlobalState['messages']['byChatId'] = {};
  const lastMessageIds = global.chats.lastMessageIds.all;
  if (!lastMessageIds) return acc;

  Object.keys(lastMessageIds).forEach((chatId) => {
    const messageId = lastMessageIds[chatId];
    const message = global.messages.byChatId[chatId]?.byId[messageId];
    if (!message) return;

    acc[chatId] = {
      byId: { [messageId]: message },
      summaryById: {},
      // Keep each thread's read state. Dropping it wiped the unread counts the chat list had just
      // reported for every chat other than the open one, and nothing refetches them: the badges came
      // back empty and only started counting again from the next incoming message — which is what a
      // fresh global (a reload, or switching to another account) always is.
      threadsById: preserveReadStates(global.messages.byChatId[chatId]?.threadsById),
    };
  });

  return acc;
}

function preserveReadStates(
  threadsById: Record<ThreadId, Thread> | undefined,
): Record<ThreadId, Thread> {
  const acc: Record<ThreadId, Thread> = {};
  if (!threadsById) return acc;

  Object.entries(threadsById).forEach(([threadId, thread]) => {
    if (!thread.readState) return;
    // Only the read state survives the reset — messages and thread info are refetched.
    acc[threadId] = { localState: {}, readState: thread.readState };
  });

  return acc;
}

function resolveDiscussionChat<T extends GlobalState>(
  global: T,
  chatId: string,
  threadId: ThreadId,
) {
  if (threadId === MAIN_THREAD_ID) return undefined;

  const chat = selectChat(global, chatId);
  if (!chat || chat.isForum || getIsSavedDialog(chatId, threadId, global.currentUserId)) return undefined;

  const threadInfo = selectThreadInfo(global, chatId, threadId);
  if (threadInfo?.isCommentsInfo === false && threadInfo.fromChannelId) {
    const originChannel = selectChat(global, threadInfo.fromChannelId);
    if (originChannel && threadInfo.fromMessageId) {
      return { chat: originChannel, messageId: threadInfo.fromMessageId };
    }
  }

  return { chat, messageId: Number(threadId) };
}

function loadTopMessages<T extends GlobalState>(
  global: T,
  chatId: string,
  threadId: ThreadId,
  viewportIds?: number[],
) {
  const currentUserId = global.currentUserId!;
  const isSavedDialog = getIsSavedDialog(chatId, threadId, currentUserId);
  const realChatId = isSavedDialog ? String(threadId) : chatId;

  const chat = selectChat(global, realChatId);
  if (!chat) return undefined;

  const readState = selectThreadReadState(global, chatId, threadId);
  const viewportAnchorId = viewportIds?.[0];
  const shouldRestoreViewport = Boolean(viewportAnchorId && !getIsSavedDialog(chatId, threadId, currentUserId));

  return callApi('fetchMessages', {
    chat,
    threadId,
    offsetId: shouldRestoreViewport ? viewportAnchorId
      : (!isSavedDialog ? readState?.lastReadInboxMessageId : undefined),
    addOffset: shouldRestoreViewport ? -(MESSAGE_LIST_SLICE + 1) : -(Math.round(MESSAGE_LIST_SLICE / 2) + 1),
    limit: shouldRestoreViewport ? (MESSAGE_LIST_SLICE + 1) : MESSAGE_LIST_SLICE,
    isSavedDialog,
  });
}

let previousGlobal: GlobalState | undefined;
// RAF can be unreliable when device goes into sleep mode, so sync logic is handled outside any component
addCallback((global: GlobalState) => {
  const { connectionState, auth, isSynced } = global;
  const { isMasterTab } = selectTabState(global);
  if (!isMasterTab || isSynced || (previousGlobal?.connectionState === connectionState
    && previousGlobal?.auth.state === auth.state)) {
    previousGlobal = global;
    return;
  }

  if (connectionState === 'connectionStateReady' && auth.state === 'authorizationStateReady') {
    getActions().sync();
  }

  previousGlobal = global;
});
